/**
 * Trucks (office) — the fleet's van inventory in one place.
 *
 *   - Counts awaiting review: variance table per count; Approve posts ONE
 *     Spire adjustment for the differences (office-approved, agreed
 *     2026-09-25); Reject sends it back with a note.
 *   - Fleet: every tech with a truck warehouse → live stock + recent
 *     transfers for that van.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, ClipboardCheck, Loader2, Search, Truck } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Feedback } from '../components/ui/feedback.js';
import { Input } from '../components/ui/input.js';
import { Pill } from '../components/ui/pill.js';
import { QueryError } from '../components/ui/query-error.js';
import { ApiError, api, type StockMove, type TruckCount, type TruckSummary } from '../lib/api.js';
import { cn } from '../lib/cn.js';
import { CountStatusPill } from './truck.js';

export function TrucksScreen() {
  const fleet = useQuery({
    queryKey: ['trucks', 'fleet'],
    queryFn: ({ signal }) => api.trucks.list(signal),
    staleTime: 60_000,
  });
  const [selected, setSelected] = useState<string | null>(null);
  const truck = (fleet.data ?? []).find((t) => t.whse === selected) ?? null;

  return (
    <div className="space-y-4">
      <header>
        <div className="text-xs text-ink-muted inline-flex items-center gap-1.5">
          <Truck className="h-3.5 w-3.5" aria-hidden /> Fleet inventory
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Trucks</h1>
      </header>

      <PendingCounts />

      <Card>
        <CardHeader>
          <CardTitle>Fleet</CardTitle>
        </CardHeader>
        <CardBody>
          {fleet.isError && <QueryError title="Couldn't load the fleet" error={fleet.error} onRetry={() => void fleet.refetch()} />}
          {fleet.data && fleet.data.length === 0 && (
            <div className="text-sm text-ink-muted">
              No techs have a truck warehouse yet. Set one per tech under Admin → Skills matrix → Edit → Truck warehouse.
            </div>
          )}
          <div className="flex flex-wrap gap-1.5">
            {(fleet.data ?? []).map((t) => (
              <button
                key={t.whse}
                type="button"
                onClick={() => setSelected(selected === t.whse ? null : t.whse)}
                aria-pressed={selected === t.whse}
                className={cn(
                  'rounded-full border px-3 py-1 text-sm',
                  selected === t.whse ? 'border-brand-200 bg-brand-50 text-ink-accent' : 'border-line-subtle text-ink hover:bg-surface-muted',
                )}
              >
                {t.techName} <span className="font-mono text-xs text-ink-muted">{t.whse}</span>
              </button>
            ))}
          </div>
        </CardBody>
      </Card>

      {truck && <TruckDetail truck={truck} />}
    </div>
  );
}

// ─── Pending counts ────────────────────────────────────────────────────────

function PendingCounts() {
  const pending = useQuery({
    queryKey: ['trucks', 'counts', 'submitted'],
    queryFn: ({ signal }) => api.trucks.counts('submitted', signal),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const recent = useQuery({
    queryKey: ['trucks', 'counts', 'all'],
    queryFn: ({ signal }) => api.trucks.counts(undefined, signal),
    staleTime: 60_000,
  });
  const reviewed = (recent.data ?? []).filter((c) => c.status !== 'submitted').slice(0, 8);

  return (
    <Card>
      <CardHeader className="flex items-center justify-between gap-2">
        <CardTitle>
          <span className="inline-flex items-center gap-1.5">
            <ClipboardCheck className="h-4 w-4 text-ink-muted" aria-hidden /> Truck counts
          </span>
        </CardTitle>
        {pending.data && pending.data.length > 0 && <Pill variant="warning" dot>{pending.data.length} to review</Pill>}
      </CardHeader>
      <CardBody className="space-y-3">
        {pending.isError && <Feedback variant="error" size="sm">{pending.error.message}</Feedback>}
        {pending.data && pending.data.length === 0 && <div className="text-sm text-ink-muted">Nothing waiting for review.</div>}
        {(pending.data ?? []).map((c) => (
          <CountReview key={c.id} count={c} />
        ))}
        {reviewed.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-ink-muted">Recently reviewed ({reviewed.length})</summary>
            <ul className="mt-2 space-y-1">
              {reviewed.map((c) => (
                <li key={c.id} className="flex items-center gap-2 text-xs">
                  <CountStatusPill c={c} />
                  <span className="text-ink">{c.techName}</span>
                  <span className="font-mono text-ink-muted">{c.whse}</span>
                  <span className="text-ink-muted">
                    {c.lineCount} lines, {c.varianceCount} off{c.spireAdjustmentNo ? ` · adj ${c.spireAdjustmentNo}` : ''}
                  </span>
                  <span className="ml-auto text-ink-subtle tabular-nums">{(c.reviewedAt ?? c.submittedAt).slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </CardBody>
    </Card>
  );
}

function CountReview({ count }: { count: TruckCount }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [showAll, setShowAll] = useState(false);
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['trucks'] });
  };
  const approve = useMutation<TruckCount, ApiError, void>({
    mutationFn: () => api.trucks.approveCount(count.id, note.trim() || undefined),
    onSuccess: invalidate,
  });
  const reject = useMutation<TruckCount, ApiError, void>({
    mutationFn: () => api.trucks.rejectCount(count.id, note.trim() || undefined),
    onSuccess: invalidate,
  });
  const busy = approve.isPending || reject.isPending;
  const lines = showAll ? count.lines : count.lines.filter((l) => l.variance !== '0');

  return (
    <div className="rounded-lg border p-3 space-y-2" style={{ borderColor: 'var(--color-border-tertiary)' }}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-ink">{count.techName}</span>
        <span className="font-mono text-xs text-ink-muted">{count.whse}</span>
        <span className="text-xs text-ink-muted">
          {new Date(count.submittedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
        </span>
        <span className="ml-auto text-xs text-ink-muted">
          {count.lineCount} counted · <span className={count.varianceCount > 0 ? 'text-amber-700 font-medium' : ''}>{count.varianceCount} differ</span>
        </span>
      </div>
      {count.note && <div className="text-xs text-ink italic">“{count.note}”</div>}

      {lines.length > 0 ? (
        <table className="w-full text-xs">
          <thead className="text-ink-subtle">
            <tr>
              <th className="text-left font-normal py-1">Part</th>
              <th className="text-right font-normal py-1">Spire</th>
              <th className="text-right font-normal py-1">Counted</th>
              <th className="text-right font-normal py-1">Adjust</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => {
              const v = Number(l.variance);
              return (
                <tr key={l.id} className="border-t" style={{ borderColor: 'var(--color-border-tertiary)' }}>
                  <td className="py-1">
                    <span className="font-mono text-ink">{l.partNo}</span>
                    {l.description && <span className="text-ink-muted"> · {l.description}</span>}
                  </td>
                  <td className="py-1 text-right tabular-nums text-ink-muted">{l.expectedQty}</td>
                  <td className="py-1 text-right tabular-nums text-ink">{l.countedQty}</td>
                  <td className={cn('py-1 text-right tabular-nums font-medium', v > 0 ? 'text-emerald-700' : v < 0 ? 'text-red-700' : 'text-ink-subtle')}>
                    {v > 0 ? `+${l.variance}` : l.variance}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className="text-xs text-ink-muted">Every counted line matches Spire.</div>
      )}
      {count.varianceCount < count.lineCount && (
        <button type="button" onClick={() => setShowAll((v) => !v)} className="text-[11px] text-ink-accent hover:underline">
          {showAll ? 'Show differences only' : `Show all ${count.lineCount} lines`}
        </button>
      )}

      <div className="flex items-center gap-2">
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to the tech (optional)" maxLength={500} className="h-9 text-xs" />
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => reject.mutate()}>
          Reject
        </Button>
        <Button size="sm" disabled={busy} onClick={() => approve.mutate()}>
          {approve.isPending ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" aria-hidden /> Posting…</> : count.varianceCount > 0 ? 'Approve & post adjustment' : 'Approve'}
        </Button>
      </div>
      {(approve.isError || reject.isError) && <Feedback variant="error" size="sm">{(approve.error ?? reject.error)?.message}</Feedback>}
    </div>
  );
}

// ─── Truck detail ──────────────────────────────────────────────────────────

function TruckDetail({ truck }: { truck: TruckSummary }) {
  const [q, setQ] = useState('');
  const stock = useQuery({
    queryKey: ['trucks', truck.whse, 'stock'],
    queryFn: ({ signal }) => api.trucks.stock(truck.whse, undefined, signal),
    staleTime: 30_000,
  });
  const transfers = useQuery({
    queryKey: ['trucks', truck.whse, 'transfers'],
    queryFn: ({ signal }) => api.trucks.transfers(truck.whse, signal),
    staleTime: 60_000,
  });
  const lines = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const all = stock.data ?? [];
    return needle ? all.filter((l) => l.partNo.toLowerCase().includes(needle) || l.description.toLowerCase().includes(needle)) : all;
  }, [stock.data, q]);

  return (
    <div className="grid gap-3 lg:grid-cols-[2fr_1fr]">
      <Card>
        <CardHeader className="flex items-center justify-between gap-2">
          <CardTitle>
            {truck.techName} · <span className="font-mono">{truck.whse}</span>
            {truck.whseDescription ? <span className="text-ink-muted font-normal"> — {truck.whseDescription}</span> : null}
          </CardTitle>
          <span className="text-xs text-ink-muted">{stock.data ? `${stock.data.length} items` : ''}</span>
        </CardHeader>
        <CardBody className="space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-subtle" aria-hidden />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter stock" className="pl-9 h-9 text-sm" aria-label="Filter stock" />
          </div>
          {stock.isError && <Feedback variant="error" size="sm">{stock.error.message}</Feedback>}
          {stock.isPending && <div className="text-xs text-ink-muted">Loading from Spire…</div>}
          <ul className="divide-y max-h-[60vh] overflow-y-auto" style={{ borderColor: 'var(--color-border-tertiary)' }}>
            {lines.map((l) => (
              <li key={l.inventoryId} className="flex items-center gap-3 py-1.5 text-sm">
                <div className="min-w-0 flex-1 leading-tight">
                  <div className="font-mono text-ink truncate">{l.partNo}</div>
                  <div className="text-xs text-ink-muted truncate">{l.description}</div>
                </div>
                <div className="text-right tabular-nums shrink-0">
                  <span className="font-semibold text-ink">{l.onHand}</span>
                  {l.uom ? <span className="text-xs text-ink-muted"> {l.uom}</span> : null}
                </div>
              </li>
            ))}
            {stock.data && lines.length === 0 && <li className="py-3 text-xs text-ink-muted">Nothing here.</li>}
          </ul>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-1.5">
              <ArrowLeftRight className="h-4 w-4 text-ink-muted" aria-hidden /> Recent transfers
            </span>
          </CardTitle>
        </CardHeader>
        <CardBody>
          {transfers.isError && <Feedback variant="error" size="sm">{transfers.error.message}</Feedback>}
          {transfers.data && transfers.data.length === 0 && <div className="text-xs text-ink-muted">None yet.</div>}
          <ul className="space-y-1.5 text-xs">
            {(transfers.data ?? []).map((t: StockMove) => (
              <li key={t.spireId} className="flex items-center gap-2">
                <Pill variant={t.posted ? (t.destinationWhse === truck.whse ? 'success' : 'info') : 'warning'}>
                  {t.destinationWhse === truck.whse ? 'In' : 'Out'}
                </Pill>
                <span className="font-mono text-ink">{t.number}</span>
                <span className="text-ink-muted truncate">
                  {t.sourceWhse} → {t.destinationWhse}
                  {t.referenceNo ? ` · ${t.referenceNo}` : ''}
                </span>
                <span className="ml-auto text-ink-subtle tabular-nums">{t.date}</span>
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>
    </div>
  );
}
